import type { JSONB } from '@risksmart-app/domain/src/types/common.types';
import { QuestionnaireTemplateVersionStatus as Questionnaire_Template_Version_Status_Enum } from '@risksmart-app/domain/src/types/consts/questionnaire-template-version-status';
import type { InsertQuestionnaireTemplateVersionMutation } from '@risksmart-app/web-graphql-client/generated/graphql';
import {
  InsertQuestionnaireTemplateVersionDocument,
  namedOperations,
} from '@risksmart-app/web-graphql-client/generated/graphql';

import { createMutationHook } from '@/utils/hook-factories';

export interface InsertQuestionnaireTemplateVersionInput {
  Version: string;
  Schema: JSONB;
  UISchema: JSONB;
  ParentId: string;
  CustomAttributeData?: Record<string, unknown> | null;
}

/**
 * Maps the tRPC mutation response to match the GraphQL mutation
 * response shape so consumers branching on the result do not need
 * to know which path executed.
 */
const mapTrpcResponseToGraphQL = (data: {
  Id: string;
}): InsertQuestionnaireTemplateVersionMutation => ({
  insert_questionnaire_template_version_one: {
    __typename: 'questionnaire_template_version',
    Id: data.Id,
  },
});

/**
 * Flag-gated wrapper for the `insertQuestionnaireTemplateVersion`
 * mutation. Defaults to the legacy Apollo mutation and delegates to
 * the tRPC variant when the `trpc` feature flag is enabled.
 *
 * `questionnaire_template_version` is node-backed; the tRPC path
 * writes asynchronously via `executeAsyncRequest`/request-state
 * tracking, but is not a governed entity, so it does not go
 * through the approval/change-request workflow.
 */
export const useInsertQuestionnaireTemplateVersion = createMutationHook({
  name: 'insertQuestionnaireTemplateVersion',
  mutationOptions: (trpc, onSuccess) =>
    trpc.frontend.questionnaireTemplateVersion.create.mutationOptions({
      onSuccess,
    }),
  mapInput: (input: InsertQuestionnaireTemplateVersionInput) => ({
    Version: input.Version,
    Schema: input.Schema,
    UISchema: input.UISchema,
    ParentId: input.ParentId,
    CustomAttributeData: input.CustomAttributeData ?? null,
  }),
  invalidate: (trpc) => [
    trpc.frontend.questionnaireTemplateVersion.pathKey(),
    trpc.frontend.questionnaireTemplate.pathKey(),
  ],
  mapResponse: (result: {
    Id: string;
  }): InsertQuestionnaireTemplateVersionMutation =>
    mapTrpcResponseToGraphQL(result),
  graphqlDocument: InsertQuestionnaireTemplateVersionDocument,
  graphqlVariables: (input: InsertQuestionnaireTemplateVersionInput) => ({
    object: {
      Version: input.Version,
      Schema: input.Schema,
      UISchema: input.UISchema,
      ParentId: input.ParentId,
      // NB: `questionnaire_template_version_insert_input` has no
      // CustomAttributeData field — the legacy insert never persisted it.
      Status: Questionnaire_Template_Version_Status_Enum.Draft,
    },
  }),
  evictFields: [
    'questionnaire_template_version',
    'questionnaire_template',
    'questionnaire_template_by_pk',
  ],
  refetchQueries: [
    namedOperations.Query.getQuestionnaireTemplateById,
    namedOperations.Query.getQuestionnaireTemplates,
  ],
});
